import { Compass } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { type CollegeDetail, type SchoolResult } from "@/services/schools";
import { Badge, SchoolDetailContent } from "./SchoolDetailContent";

interface SchoolProgramDetailsPanelProps {
  school: SchoolResult;
  userGenderLabel: string;
  collegeData: CollegeDetail | null;
  collegeLoading: boolean;
  className?: string;
}

export function SchoolProgramDetailsPanel({
  school,
  userGenderLabel,
  collegeData,
  collegeLoading,
  className = "",
}: SchoolProgramDetailsPanelProps) {
  const navigate = useNavigate();
  const affiliationParts: string[] = [];
  const seen = new Set<string>();
  for (const raw of [school.association?.toUpperCase(), school.division, school.conference]) {
    if (raw && !seen.has(raw.toLowerCase())) {
      seen.add(raw.toLowerCase());
      affiliationParts.push(raw);
    }
  }

  function findSimilarSchools() {
    navigate("/chat", {
      state: {
        autoPrompt: `Find schools similar to ${school.schoolName} that I should consider. Use my current golf data to keep the recommendations credible, include Dream, Realistic, and Safety options where the evidence supports them, and briefly explain what makes each school similar.`,
        threadTitle: `Schools like ${school.schoolName}`,
      },
    });
  }

  return (
    <section className={`rounded-2xl border border-border bg-white p-4 sm:p-5 ${className}`}>
      <div className="mb-4">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-subtle">
          Program details
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {affiliationParts.map(part => (
            <Badge key={part} variant="primary">
              {part}
            </Badge>
          ))}
          {collegeData?.ownership && <Badge>{collegeData.ownership}</Badge>}
          {collegeData?.open_admissions && <Badge>Open Admissions</Badge>}
        </div>
      </div>

      <SchoolDetailContent
        school={school}
        userGenderLabel={userGenderLabel}
        collegeData={collegeData}
        collegeLoading={collegeLoading}
      />

      <div className="mt-5 border-t border-border pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={findSimilarSchools}
          className="h-10 w-full gap-2 rounded-xl border-border text-[11px] font-extrabold text-forest-soft hover:bg-surface"
        >
          <Compass className="h-4 w-4 text-primary-text" /> Find similar schools
        </Button>
      </div>
    </section>
  );
}
